import React, { useRef, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Pencil } from 'lucide-react';
import type { Stamp } from '../hooks/useStamps';
import { renderShapeToSvgDataUrl } from '../utils/stampShapes';
import { useIsMobile } from '../../../hooks/useIsMobile';
import TextPopover from './TextPopover';
import StampShapePopover from './StampShapePopover';

/**
 * 把 popover 用 portal 渲染到 body，並建立一個 fixed 包裝盒對齊 stamp 的位置與大小。
 * 內部 popover 的 `absolute right-full / left-full top-0` 會相對於這個 fixed 包裝盒，
 * 因此可以脫離 imageContainerRef 的 overflow-hidden、不會推擠到預覽圖。
 */
const AnchoredPortal: React.FC<{ anchorRef: React.RefObject<HTMLElement | null>; children: React.ReactNode }> = ({ anchorRef, children }) => {
    const [rect, setRect] = useState<DOMRect | null>(null);
    useEffect(() => {
        const update = () => {
            if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
        };
        update();
        const ro = new ResizeObserver(update);
        if (anchorRef.current) ro.observe(anchorRef.current);
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            ro.disconnect();
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [anchorRef]);
    if (!rect) return null;
    return createPortal(
        <div style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            zIndex: 100,
            // 不擋住下方 stamp 的指標事件；popover 子元素自帶背景，會自己接收事件
            pointerEvents: 'none',
        }}>
            <div style={{ pointerEvents: 'auto', width: '100%', height: '100%', position: 'relative' }}>
                {children}
            </div>
        </div>,
        document.body,
    );
};

type ResizeDir = { key: string; hx: -1 | 0 | 1; hy: -1 | 0 | 1; cursor: string; pos: string };

// 8 個縮放把手：四角（等比縮放，維持長寬比）＋ 四邊中點（自由變形，只改單一軸）
const RESIZE_HANDLES: ResizeDir[] = [
    { key: 'nw', hx: -1, hy: -1, cursor: 'nwse-resize', pos: '-top-1.5 -left-1.5' },
    { key: 'n', hx: 0, hy: -1, cursor: 'ns-resize', pos: '-top-1.5 left-1/2 -translate-x-1/2' },
    { key: 'ne', hx: 1, hy: -1, cursor: 'nesw-resize', pos: '-top-1.5 -right-1.5' },
    { key: 'e', hx: 1, hy: 0, cursor: 'ew-resize', pos: 'top-1/2 -right-1.5 -translate-y-1/2' },
    { key: 'se', hx: 1, hy: 1, cursor: 'nwse-resize', pos: '-bottom-1.5 -right-1.5' },
    { key: 's', hx: 0, hy: 1, cursor: 'ns-resize', pos: '-bottom-1.5 left-1/2 -translate-x-1/2' },
    { key: 'sw', hx: -1, hy: 1, cursor: 'nesw-resize', pos: '-bottom-1.5 -left-1.5' },
    { key: 'w', hx: -1, hy: 0, cursor: 'ew-resize', pos: 'top-1/2 -left-1.5 -translate-y-1/2' },
];

interface DraggableStampProps {
    stamp: Stamp;
    containerRef: React.RefObject<HTMLDivElement | null>;
    onUpdate: (id: string, partial: Partial<Omit<Stamp, 'id' | 'type'>>) => void;
    onRemove: (id: string) => void;
    onRequestEyedropper?: (callback: (color: string) => void) => void;
    popoverPlacement?: 'left' | 'right';
}

const DraggableStamp: React.FC<DraggableStampProps> = ({ stamp, containerRef, onUpdate, onRemove, onRequestEyedropper, popoverPlacement = 'left' }) => {
    const isMobile = useIsMobile();
    // 手機改用 bottom sheet 形式的 popover，避免錨點靠右時被推出畫面
    const effectivePopoverPlacement = isMobile ? 'bottom' : popoverPlacement;
    const [isEditingText, setIsEditingText] = useState(false);
    const [isEditingShape, setIsEditingShape] = useState(false);
    const hasTextData = !!stamp.textData;
    const hasShapeMeta = !!stamp.shapeMeta;
    const isEditable = hasTextData || hasShapeMeta;
    const stampWrapperRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const isResizing = useRef(false);
    const dragStart = useRef({ x: 0, y: 0, xPct: 0, yPct: 0 });
    // resize 起點：記錄按下當下的位置/尺寸（pct 與 px）、把手方向與是否等比
    const resizeStart = useRef({
        x: 0, y: 0,
        w: 0, h: 0, left: 0, top: 0,
        wPx: 1, hPx: 1,
        hx: 0 as -1 | 0 | 1, hy: 0 as -1 | 0 | 1,
        constrain: false,
    });
    const hasExplicitHeight = stamp.heightPct !== undefined;

    const getContainerSize = useCallback(() => {
        const el = containerRef.current;
        if (!el) return { w: 1, h: 1 };
        const rect = el.getBoundingClientRect();
        return { w: rect.width, h: rect.height };
    }, [containerRef]);

    // ── Drag ──────────────────────────────────────────────────────────────────
    const onDragMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isDragging.current = true;
        dragStart.current = { x: e.clientX, y: e.clientY, xPct: stamp.xPct, yPct: stamp.yPct };

        const onMove = (ev: MouseEvent) => {
            if (!isDragging.current) return;
            const { w, h } = getContainerSize();
            const dx = ((ev.clientX - dragStart.current.x) / w) * 100;
            const dy = ((ev.clientY - dragStart.current.y) / h) * 100;
            onUpdate(stamp.id, {
                xPct: dragStart.current.xPct + dx,
                yPct: dragStart.current.yPct + dy,
            });
        };
        const onUp = () => {
            isDragging.current = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            // 吃掉拖移結束後的 click，避免觸發外層放大圖片
            const swallowClick = (e: MouseEvent) => {
                e.stopPropagation();
                window.removeEventListener('click', swallowClick, true);
            };
            window.addEventListener('click', swallowClick, true);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [stamp, onUpdate, getContainerSize]);

    // ── Touch drag ────────────────────────────────────────────────────────────
    const onDragTouchStart = useCallback((e: React.TouchEvent) => {
        e.stopPropagation();
        const touch = e.touches[0];
        dragStart.current = { x: touch.clientX, y: touch.clientY, xPct: stamp.xPct, yPct: stamp.yPct };

        const onMove = (ev: TouchEvent) => {
            const t = ev.touches[0];
            const { w, h } = getContainerSize();
            const dx = ((t.clientX - dragStart.current.x) / w) * 100;
            const dy = ((t.clientY - dragStart.current.y) / h) * 100;
            onUpdate(stamp.id, {
                xPct: dragStart.current.xPct + dx,
                yPct: dragStart.current.yPct + dy,
            });
        };
        const onEnd = () => {
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
        };
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onEnd);
    }, [stamp, onUpdate, getContainerSize]);

    // ── Resize ────────────────────────────────────────────────────────────────
    // 四角把手：等比縮放（維持拖曳當下的長寬比）；四邊把手：自由變形（只改單一軸）。
    // 縮放一律寫入 sizePct 與 heightPct；拖上緣/左緣時同步位移 xPct/yPct，讓對側邊固定。
    // 初次 resize 時若沒有 heightPct，先從 DOM 量目前顯示比例當作起點。
    const beginResize = useCallback((dir: ResizeDir, clientX: number, clientY: number) => {
        isResizing.current = true;
        let hPct = stamp.heightPct;
        if (hPct === undefined) {
            const wrap = stampWrapperRef.current;
            const cont = containerRef.current;
            hPct = wrap && cont ? (wrap.offsetHeight / cont.offsetHeight) * 100 : stamp.sizePct;
        }
        const { w: contW, h: contH } = getContainerSize();
        resizeStart.current = {
            x: clientX,
            y: clientY,
            w: stamp.sizePct,
            h: hPct,
            left: stamp.xPct,
            top: stamp.yPct,
            wPx: (stamp.sizePct / 100) * contW,
            hPx: (hPct / 100) * contH,
            hx: dir.hx,
            hy: dir.hy,
            constrain: dir.hx !== 0 && dir.hy !== 0,
        };
    }, [stamp, containerRef, getContainerSize]);

    // 依指標位置計算新尺寸與位置並回寫
    const applyResize = useCallback((clientX: number, clientY: number) => {
        if (!isResizing.current) return;
        const rs = resizeStart.current;
        const { w: contW, h: contH } = getContainerSize();
        const dxPx = clientX - rs.x;
        const dyPx = clientY - rs.y;

        let newW: number;
        let newH: number;
        if (rs.constrain) {
            // 把指標位移投影到方框對角線，維持長寬比等比縮放
            const diag = Math.hypot(rs.wPx, rs.hPx) || 1;
            const proj = (dxPx * rs.hx * rs.wPx + dyPx * rs.hy * rs.hPx) / diag;
            const minScale = Math.max(5 / rs.w, 5 / rs.h);
            const scale = Math.max((diag + proj) / diag, minScale);
            newW = rs.w * scale;
            newH = rs.h * scale;
        } else {
            const dxPct = (dxPx / contW) * 100;
            const dyPct = (dyPx / contH) * 100;
            newW = Math.max(5, rs.w + rs.hx * dxPct);
            newH = Math.max(5, rs.h + rs.hy * dyPct);
        }

        const patch: Partial<Omit<Stamp, 'id' | 'type'>> = { sizePct: newW, heightPct: newH };
        // 拖左緣 / 上緣時維持對側邊固定 → 需同步位移左上角
        if (rs.hx === -1) patch.xPct = rs.left + (rs.w - newW);
        if (rs.hy === -1) patch.yPct = rs.top + (rs.h - newH);
        onUpdate(stamp.id, patch);
    }, [stamp.id, onUpdate, getContainerSize]);

    const onResizeMouseDown = useCallback((dir: ResizeDir) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        beginResize(dir, e.clientX, e.clientY);

        const onMove = (ev: MouseEvent) => applyResize(ev.clientX, ev.clientY);
        const onUp = () => {
            isResizing.current = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            const swallowClick = (e: MouseEvent) => {
                e.stopPropagation();
                window.removeEventListener('click', swallowClick, true);
            };
            window.addEventListener('click', swallowClick, true);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [beginResize, applyResize]);

    // ── Touch resize：手機用 ──
    const onResizeTouchStart = useCallback((dir: ResizeDir) => (e: React.TouchEvent) => {
        // 阻斷外層拖移觸發
        e.stopPropagation();
        const touch = e.touches[0];
        beginResize(dir, touch.clientX, touch.clientY);

        const onMove = (ev: TouchEvent) => {
            if (!isResizing.current) return;
            ev.preventDefault();
            const t = ev.touches[0];
            applyResize(t.clientX, t.clientY);
        };
        const onEnd = () => {
            isResizing.current = false;
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
            window.removeEventListener('touchcancel', onEnd);
        };
        // passive: false 才能在 onMove 內 preventDefault 阻止頁面捲動
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onEnd);
        window.addEventListener('touchcancel', onEnd);
    }, [beginResize, applyResize]);

    const style: React.CSSProperties = {
        position: 'absolute',
        left: `${stamp.xPct}%`,
        top: `${stamp.yPct}%`,
        width: `${stamp.sizePct}%`,
        // 形狀印章未設 heightPct 時強制 1:1，避免 SVG intrinsic 在不同瀏覽器表現不一致
        ...(hasExplicitHeight
            ? { height: `${stamp.heightPct}%` }
            : hasShapeMeta ? { aspectRatio: '1 / 1' } : {}),
        cursor: 'grab',
        userSelect: 'none',
        touchAction: 'none',
        pointerEvents: 'auto',
    };

    return (
        <div
            ref={stampWrapperRef}
            style={style}
            onMouseDown={onDragMouseDown}
            onTouchStart={onDragTouchStart}
            onClick={e => e.stopPropagation()}
            className="group"
        >
            {/* Delete button — 觸控裝置（無 hover）永遠顯示 */}
            <button
                className="absolute -top-2.5 -right-2.5 z-10 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity shadow-md"
                onClick={e => { e.stopPropagation(); onRemove(stamp.id); }}
                onMouseDown={e => e.stopPropagation()}
                onTouchStart={e => e.stopPropagation()}
            >
                <X size={10} />
            </button>

            {/* Edit button — 文字印章 / 形狀印章顯示 */}
            {isEditable && (
                <button
                    className="absolute -top-2.5 -left-2.5 z-10 w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity shadow-md"
                    onClick={e => {
                        e.stopPropagation();
                        if (hasTextData) setIsEditingText(true);
                        else if (hasShapeMeta) setIsEditingShape(true);
                    }}
                    onMouseDown={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    title={hasTextData ? '編輯文字' : '編輯印章'}
                >
                    <Pencil size={10} />
                </button>
            )}

            {/* Text edit popover — 用 portal 渲染到 body，脫離 imageContainerRef 的 overflow-hidden */}
            {isEditingText && stamp.textData && (
                <AnchoredPortal anchorRef={stampWrapperRef}>
                    <div onMouseDown={e => e.stopPropagation()}>
                        <TextPopover
                            initialValues={stamp.textData}
                            onSubmit={(dataUrl, textData) => {
                                onUpdate(stamp.id, { imageDataUrl: dataUrl, textData });
                            }}
                            onClose={() => setIsEditingText(false)}
                            onRequestEyedropper={onRequestEyedropper}
                            placement={effectivePopoverPlacement}
                        />
                    </div>
                </AnchoredPortal>
            )}

            {/* Shape edit popover — 用 portal 渲染到 body，脫離 imageContainerRef 的 overflow-hidden */}
            {isEditingShape && stamp.shapeMeta && (
                <AnchoredPortal anchorRef={stampWrapperRef}>
                    <div onMouseDown={e => e.stopPropagation()}>
                        <StampShapePopover
                            initialValues={stamp.shapeMeta}
                            onSubmit={(_dataUrl, shapeMeta) => {
                                // 重新生成符合新 shape 的 svg dataUrl
                                const newDataUrl = renderShapeToSvgDataUrl(
                                    shapeMeta.kind, shapeMeta.color, shapeMeta.lineWidth, shapeMeta.filled,
                                );
                                onUpdate(stamp.id, { imageDataUrl: newDataUrl, shapeMeta });
                            }}
                            onClose={() => setIsEditingShape(false)}
                            onRequestEyedropper={onRequestEyedropper}
                            placement={effectivePopoverPlacement}
                        />
                    </div>
                </AnchoredPortal>
            )}

            {/* Stamp content — 形狀 / 已縮放過的印章用 h-full + object-fill；其他用 h-auto 保留自然比例。
                所有印章一律不加陰影：印章常被用來遮蔽內容，陰影會讓遮蔽邊緣突兀。 */}
            {(() => {
                const stretchMode = hasExplicitHeight || hasShapeMeta;
                return (
                    <div className={`w-full pointer-events-none select-none ${stretchMode ? 'h-full' : 'h-auto'}`}>
                        {stamp.imageDataUrl && (
                            <img
                                src={stamp.imageDataUrl}
                                alt="stamp"
                                className={`w-full block ${stretchMode ? 'h-full' : 'h-auto'}`}
                                style={stretchMode ? { objectFit: 'fill' } : undefined}
                                draggable={false}
                            />
                        )}
                    </div>
                );
            })()}

            {/* Resize handles — 四角等比、四邊自由變形；觸控裝置加大並永遠顯示 */}
            {RESIZE_HANDLES.map(hd => (
                <div
                    key={hd.key}
                    className={`absolute ${hd.pos} w-3 h-3 [@media(hover:none)]:w-5 [@media(hover:none)]:h-5 bg-white border-2 border-indigo-500 rounded-full opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity shadow`}
                    style={{ cursor: hd.cursor, touchAction: 'none' }}
                    onMouseDown={onResizeMouseDown(hd)}
                    onTouchStart={onResizeTouchStart(hd)}
                />
            ))}
        </div>
    );
};

export default DraggableStamp;
